import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { AttuneWrapup } from '../../../../lib/attune/experience';

// The end-of-lesson reflective wrap-up — a scroll-through that mirrors the
// learner's own thinking back to them (their words → a strength + the real gap →
// how they like to learn → the one thing to watch), then hands them a choice of
// route into the personalised activity. Faithful to Dylan's mockup; its bespoke
// cinematic palette (.attw-* in attune.css) is deliberate — this is the one part
// of the course made only for them, so it gets its own look rather than the
// course theme. The path choice lives INSIDE the flow (resolving the old
// detached "This feels right" button).

export type WrapupRoute = 'method' | 'scenario' | 'questions';

// Both build routes now make a SCENARIO — that's end-of-lesson Attune's whole
// promise (your words become the scene) — so the descriptions differentiate on
// depth (quick interactive vs framed mini-lesson), not on kind. Honest copy:
// the old 'method walkthrough' line described a shape we never actually built.
const PATHS: { route: WrapupRoute; icon: string; title: string; desc: string }[] = [
  { route: 'method', icon: '⏱', title: 'The 10-minute version', desc: 'A short playable scenario from your situation — straight in, nothing around it.' },
  { route: 'scenario', icon: '🧩', title: 'Walk me through a real example', desc: 'A playable scenario drawn from your own week — decisions, consequences, the lot.' },
  { route: 'questions', icon: '💬', title: 'I still have questions first', desc: 'A little more conversation before we build anything.' },
];

export function AttuneWrapupPanel({
  wrapup,
  thin = false,
  onPick,
}: {
  wrapup: AttuneWrapup;
  /** Thin diagnostic signal (a few one-word answers): drop the panels that
   *  would perform certainty we don't have — the quote reading and the
   *  trip-up reveal — and let the route choice carry the personalisation. */
  thin?: boolean;
  onPick: (route: WrapupRoute) => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [seen, setSeen] = useState<Set<number>>(() => new Set([0]));
  const [active, setActive] = useState(0);
  const [revealed, setRevealed] = useState(false);
  // Dylan's mockup pairs the full-height panels with a "Scroll" hint that fades
  // once the learner moves past the first panel — the affordance that makes the
  // long, deliberate scroll read as intentional rather than empty.
  const [scrolled, setScrolled] = useState(false);

  // Fade each panel in as it scrolls into view, and track the active one for the
  // progress dots. Root is the wrap-up's own scroll container (it overlays the
  // Attune surface), not the document.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const panels = Array.from(root.querySelectorAll<HTMLElement>('[data-attw-panel]'));
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          const idx = panels.indexOf(e.target as HTMLElement);
          if (idx < 0 || !e.isIntersecting) return;
          setSeen((prev) => (prev.has(idx) ? prev : new Set(prev).add(idx)));
          setActive(idx);
        });
      },
      { root, threshold: 0.5 },
    );
    panels.forEach((p) => io.observe(p));
    return () => io.disconnect();
  }, []);

  const inClass = (i: number) => `attw-panel${seen.has(i) ? ' in' : ''}`;

  // Panels as render functions over their SEQUENTIAL index, so dropping the
  // thin-signal panels keeps the progress dots and reveal indices honest.
  const panels: ((i: number) => ReactNode)[] = [
    // opening
    (i) => (
      <section key="open" data-attw-panel className={inClass(i)}>
        <div className="attw-eyebrow">Attune · after your conversation</div>
        <h1 className="attw-h1">{wrapup.openingHeadline}</h1>
        <p className="attw-lead">{wrapup.openingLead}</p>
      </section>
    ),
    // their own words — only when the conversation gave us a real quote
    ...(thin
      ? []
      : [
          (i: number) => (
            <section key="quote" data-attw-panel className={inClass(i)}>
              <div className="attw-eyebrow gold">In your words</div>
              <blockquote className="attw-quote">
                “{wrapup.quote}”
                <span className="attw-qsrc">— you</span>
              </blockquote>
              <p className="attw-body">{wrapup.quoteReading}</p>
            </section>
          ),
        ]),
    // what you walked in with
    (i) => (
      <section key="cards" data-attw-panel className={inClass(i)}>
        <div className="attw-eyebrow">What you walked in with</div>
        <p className="attw-body" style={{ marginBottom: 18 }}>Two things stood out — and they pull in opposite directions.</p>
        <div className="attw-cards">
          <div className={`attw-card have${seen.has(i) ? ' in' : ''}`}>
            <div className="attw-tag">Already strong</div>
            <h3>{wrapup.strengthTitle}</h3>
            <p>{wrapup.strengthBody}</p>
          </div>
          <div className={`attw-card gap${seen.has(i) ? ' in' : ''}`}>
            <div className="attw-tag">The real gap</div>
            <h3>{wrapup.gapTitle}</h3>
            <p>{wrapup.gapBody}</p>
          </div>
        </div>
      </section>
    ),
    // how you like to learn
    (i) => (
      <section key="meter" data-attw-panel className={inClass(i)}>
        <div className="attw-eyebrow">How you like to learn</div>
        <div className="attw-meter-wrap">
          <div className="attw-meter-label">
            <span>{wrapup.meterLeft}</span>
            <span>{wrapup.meterRight}</span>
          </div>
          <div className="attw-meter">
            <div className="attw-fill" style={{ width: seen.has(i) ? `${wrapup.meterValue}%` : '0%' }} />
          </div>
        </div>
        {wrapup.chips.length > 0 && (
          <div className="attw-chips">
            {wrapup.chips.map((c, ci) => (
              <span key={ci} className="attw-chip">{c}</span>
            ))}
          </div>
        )}
      </section>
    ),
    // the one thing likely to trip you up (gated) — needs real signal to name
    ...(thin
      ? []
      : [
          (i: number) => (
            <section key="tripup" data-attw-panel className={inClass(i)}>
              <div className="attw-eyebrow terra">The one thing likely to trip you up</div>
              <p className="attw-body">There’s a pattern in how you talked about this that’s worth naming before you start.</p>
              {!revealed ? (
                <button className="attw-reveal" onClick={() => setRevealed(true)}>Show me</button>
              ) : (
                <div className="attw-insight">
                  <h3>{wrapup.tripupTitle}</h3>
                  <p>{wrapup.tripupBody}</p>
                </div>
              )}
            </section>
          ),
        ]),
    // hand-off to the choice
    (i) => (
      <section key="paths" data-attw-panel className={inClass(i)}>
        <div className="attw-eyebrow">So, where to start</div>
        <h1 className="attw-h1 sm">So let’s build from this. <span className="attw-accent">You pick the way in.</span></h1>
        <p className="attw-lead">Same destination — different route, based on how you said you like to work.</p>
        <div className="attw-paths">
          {PATHS.map((p) => (
            <button key={p.route} className="attw-path" onClick={() => onPick(p.route)}>
              <span className="attw-ic" aria-hidden>{p.icon}</span>
              <span className="attw-ptxt">
                <b>{p.title}</b>
                <span>{p.desc}</span>
              </span>
              <span className="attw-arrow" aria-hidden>→</span>
            </button>
          ))}
        </div>
      </section>
    ),
  ];

  return (
    <div
      className="attw"
      ref={rootRef}
      onScroll={(e) => setScrolled(e.currentTarget.scrollTop > 120)}
    >
      <div className="attw-progress">
        {panels.map((_, i) => (
          <span key={i} className={`attw-seg${i === active ? ' on' : ''}${i < active ? ' done' : ''}`} />
        ))}
      </div>

      {panels.map((render, i) => render(i))}

      <div className={`attw-hint${scrolled ? ' gone' : ''}`} aria-hidden>
        <span>Scroll</span>
        <span className="attw-hint-arrow" />
      </div>
    </div>
  );
}
